import React from 'react';
import {
  Button,
  TextField,
  Stack,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Switch,
  Checkbox,
  Autocomplete,
  Typography,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { manufacturersApi, productEquivalencyCategoriesApi, productEquivalenciesApi, productsApi } from '../../api/services';
import type { Product, ProductEquivalency } from '../../api/config';
import { useDebounce } from '../../hooks/useDebounce';
import { FormDialog } from '../FormDialog';

interface ProductFormData {
  sku: string;
  name: string;
  product_equivalency_category_id?: number;
  product_equivalency_id?: number;
  hide_when_zero?: boolean;
  manufacturer_id?: number;
  reorder_threshold: number;
  unit_cost: number | null;
  units_per_box: number | null;
}

interface ProductFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Product> & { product_equivalency_category_id?: number; product_equivalency_id?: number; hide_when_zero?: boolean }) => void;
  initialData?: Product;
  error?: string | null;
  isPending?: boolean;
}

export const ProductForm: React.FC<ProductFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  error,
  isPending,
}) => {
  const queryClient = useQueryClient();
  const isEditing = !!initialData;
  const [formData, setFormData] = React.useState<ProductFormData>({
    sku: '',
    name: '',
    product_equivalency_category_id: undefined,
    product_equivalency_id: undefined,
    manufacturer_id: undefined,
    reorder_threshold: 0,
    unit_cost: 0,
    units_per_box: null,
  });
  const [hasEquivalent, setHasEquivalent] = React.useState(false);
  const [selectedGroup, setSelectedGroup] = React.useState<ProductEquivalency | null>(null);
  const [newManufacturerName, setNewManufacturerName] = React.useState('');
  const [isAddingManufacturer, setIsAddingManufacturer] = React.useState(false);
  const [newCategoryName, setNewCategoryName] = React.useState('');
  const [isAddingCategory, setIsAddingCategory] = React.useState(false);

  const { data: manufacturers = [] } = useQuery({
    queryKey: ['manufacturers'],
    queryFn: manufacturersApi.getAll,
    enabled: open,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['product_equivalency_categories'],
    queryFn: productEquivalencyCategoriesApi.getAll,
    enabled: open,
  });

  const { data: productEquivalencies = [] } = useQuery({
    queryKey: ['product_equivalencies'],
    queryFn: () => productEquivalenciesApi.getAll(),
    enabled: open && !isEditing,
  });

  const createManufacturerMutation = useMutation({
    mutationFn: manufacturersApi.create,
    onSuccess: (newManufacturer) => {
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      setFormData((prev) => ({ ...prev, manufacturer_id: newManufacturer.id }));
      setNewManufacturerName('');
      setIsAddingManufacturer(false);
    },
  });

  const createCategoryMutation = useMutation({
    mutationFn: productEquivalencyCategoriesApi.create,
    onSuccess: (newCategory) => {
      queryClient.invalidateQueries({ queryKey: ['product_equivalency_categories'] });
      setFormData((prev) => ({ ...prev, product_equivalency_category_id: newCategory.id }));
      setNewCategoryName('');
      setIsAddingCategory(false);
    },
  });

  React.useEffect(() => {
    if (initialData) {
      setFormData({
        sku: initialData.sku,
        name: initialData.name,
        product_equivalency_category_id: initialData.product_equivalency?.product_equivalency_category?.id,
        product_equivalency_id: undefined,
        manufacturer_id: initialData.manufacturer_id,
        reorder_threshold: initialData.reorder_threshold,
        unit_cost: initialData.unit_cost,
        units_per_box: initialData.units_per_box ?? null,
      });
    } else {
      setFormData({
        sku: '',
        name: '',
        product_equivalency_category_id: undefined,
        product_equivalency_id: undefined,
        manufacturer_id: undefined,
        reorder_threshold: 0,
        unit_cost: 0,
        units_per_box: null,
      });
    }
    setHasEquivalent(false);
    setSelectedGroup(null);
    setIsAddingManufacturer(false);
    setNewManufacturerName('');
    setIsAddingCategory(false);
    setNewCategoryName('');
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'units_per_box'
        ? (value === '' ? null : Number(value))
        : name === 'reorder_threshold' || name === 'unit_cost'
          ? parseFloat(value) || 0
          : value,
    }));
  };

  const [categoryError, setCategoryError] = React.useState(false);

  const debouncedSku = useDebounce(formData.sku.trim(), 400);

  const { data: skuCheck } = useQuery({
    queryKey: ['product_check_sku', debouncedSku],
    queryFn: () => productsApi.checkSku(debouncedSku),
    enabled: open && !isEditing && !!debouncedSku,
  });

  // Guard against stale React Query cache: in edit mode the check query is
  // disabled, but cached data from a prior create-mode check with the same
  // query key can leak through. Always treat edit mode as "not taken".
  const skuIsTaken = !isEditing && (skuCheck?.exists ?? false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.product_equivalency_id && !formData.product_equivalency_category_id) {
      setCategoryError(true);
      return;
    }
    setCategoryError(false);
    onSubmit(formData);
  };

  const handleAddManufacturer = () => {
    if (!newManufacturerName.trim()) return;
    createManufacturerMutation.mutate({ name: newManufacturerName.trim() });
  };

  const handleAddCategory = () => {
    if (!newCategoryName.trim()) return;
    createCategoryMutation.mutate({ name: newCategoryName.trim() });
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={initialData ? 'Edit Product' : 'Create New Product'}
      submitLabel={initialData ? 'Update' : 'Create'}
      error={error}
      isPending={isPending}
      submitDisabled={skuIsTaken}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              name="sku"
              label="SKU"
              value={formData.sku}
              onChange={handleChange}
              fullWidth
              required
              disabled={!!initialData}
              error={skuIsTaken}
              helperText={skuIsTaken ? 'This SKU is already in use.' : undefined}
            />
            <TextField
              name="name"
              label="Name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
            />
            {!isEditing && (
              <FormControlLabel
                control={
                  <Switch
                    checked={hasEquivalent}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setHasEquivalent(checked);
                      if (!checked) {
                        setSelectedGroup(null);
                        setFormData((prev) => ({
                          ...prev,
                          product_equivalency_id: undefined,
                          product_equivalency_category_id: undefined,
                          hide_when_zero: undefined,
                        }));
                      }
                    }}
                  />
                }
                label="This product has an equivalent"
              />
            )}
            {!isEditing && hasEquivalent && (
              <Autocomplete
                options={productEquivalencies}
                value={selectedGroup}
                onChange={(_e, group) => {
                  setSelectedGroup(group);
                  if (group) {
                    setFormData((prev) => ({
                      ...prev,
                      product_equivalency_id: group.id,
                      product_equivalency_category_id: group.product_equivalency_category_id,
                    }));
                    setCategoryError(false);
                  } else {
                    setFormData((prev) => ({
                      ...prev,
                      product_equivalency_id: undefined,
                      product_equivalency_category_id: undefined,
                    }));
                  }
                }}
                getOptionLabel={(option) =>
                  option.hexa_hq_sku ? `${option.name} (${option.hexa_hq_sku})` : option.name
                }
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack>
                      <Typography variant="body2">
                        {option.hexa_hq_sku ? `${option.name} (${option.hexa_hq_sku})` : option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.product_equivalency_category?.name ?? 'No category'}
                      </Typography>
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product Equivalency"
                    placeholder="Search by equivalency name or SKU..."
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                fullWidth
              />
            )}
            {!isEditing && hasEquivalent && selectedGroup && (
              <Tooltip title="Hide this product from the restock digest when its quantity is 0">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!formData.hide_when_zero}
                      onChange={(e) => setFormData((prev) => ({ ...prev, hide_when_zero: e.target.checked }))}
                    />
                  }
                  label="Hide on digest when qty is zero"
                />
              </Tooltip>
            )}
            <FormControl fullWidth required={!formData.product_equivalency_id} error={categoryError} disabled={hasEquivalent && !!selectedGroup}>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.product_equivalency_category_id ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '__add_new__') {
                    setIsAddingCategory(true);
                    return;
                  }
                  const newVal = val === '' ? undefined : Number(val);
                  setFormData((prev) => ({
                    ...prev,
                    product_equivalency_category_id: newVal,
                  }));
                  if (newVal) setCategoryError(false);
                }}
                label="Category"
              >
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem value="__add_new__">
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <AddIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Add New Category</ListItemText>
                </MenuItem>
              </Select>
              {categoryError && !formData.product_equivalency_id && <FormHelperText>Category is required</FormHelperText>}
            </FormControl>
            {isAddingCategory && (
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label="New Category Name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  size="small"
                  fullWidth
                  autoFocus
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddCategory}
                  disabled={!newCategoryName.trim() || createCategoryMutation.isPending}
                >
                  Add
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setIsAddingCategory(false);
                    setNewCategoryName('');
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            )}
            <FormControl fullWidth>
              <InputLabel>Manufacturer</InputLabel>
              <Select
                value={formData.manufacturer_id ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '__add_new__') {
                    setIsAddingManufacturer(true);
                    return;
                  }
                  setFormData((prev) => ({
                    ...prev,
                    manufacturer_id: val === '' ? undefined : Number(val),
                  }));
                }}
                label="Manufacturer"
              >
                <MenuItem value="">None</MenuItem>
                {manufacturers.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.name}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem value="__add_new__">
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <AddIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Add New Manufacturer</ListItemText>
                </MenuItem>
              </Select>
            </FormControl>
            {isAddingManufacturer && (
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label="New Manufacturer Name"
                  value={newManufacturerName}
                  onChange={(e) => setNewManufacturerName(e.target.value)}
                  size="small"
                  fullWidth
                  autoFocus
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddManufacturer}
                  disabled={!newManufacturerName.trim() || createManufacturerMutation.isPending}
                >
                  Add
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setIsAddingManufacturer(false);
                    setNewManufacturerName('');
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            )}
            <TextField
              name="units_per_box"
              label="Units per box"
              type="number"
              value={formData.units_per_box ?? ''}
              onChange={handleChange}
              fullWidth
              inputProps={{ min: 2 }}
              helperText="Sellable units per box for box-level-tagged products; blank = single-unit."
            />
          </Stack>
    </FormDialog>
  );
};
